import { useState } from 'react'
import { Link, PageProps } from 'gatsby'
import clsx from 'clsx'
import {
  CustomerQueryInput,
  CustomersQueryVariables,
  CustomerType,
} from 'lib/graphql/storeadmin'
import { useCustomers } from 'lib/hooks'
import { CustomerSearch, CustomersTable } from 'components/customers'
import {
  corporateSelectedOption,
  customerTypeInput,
  staticCustomerType,
} from 'lib/constants'
import {
  Button,
  Card,
  Error,
  Pagination,
  SEO,
  Space,
  Title,
} from 'components/common'
import { defaultQueryVariables } from '../personal'

export const Head = () => <SEO title="顧客（法人・団体）管理" />

enum BookableFlagSortingType {
  All = 'all',
  Bookable = 'bookable',
  UnBookable = 'unBookable',
}

const IndexPage = (props: PageProps<Queries.Query>): JSX.Element => {
  //search
  const [warning, setWarning] = useState<string>(null)
  const [queryVariables, setQueryVariables] = useState<CustomersQueryVariables>(
    {
      input: [],
    }
  )

  // Pagination & Data Source
  const { data, loading, error, goto, page, size, total, paginate, refetch } =
    useCustomers({
      initialSize: 10,
      queryOptions: {
        variables: { ...queryVariables, type: CustomerType.Corporate },
      },
    })

  // Search function
  const onSearch = (searchField: string, input: CustomerQueryInput) => {
    if (!searchField) {
      setWarning('絞り込みたい内容を入力してください')
      return
    }
    goto(1)
    setWarning(null)
    setQueryVariables({
      input: [
        input,
        { ...customerTypeInput, value: staticCustomerType.corporate },
      ],
    })
  }
  const onSearchClear = () => {
    if (data?.length > 0) {
      goto(1)
    }
    setWarning(null)
    setQueryVariables(null)
  }

  /**
   * 予約可能と予約不可のタブをクリックした時の処理
   */
  const onTabChange = async (key: BookableFlagSortingType): Promise<void> => {
    goto(1)
    try {
      switch (key) {
        case BookableFlagSortingType.Bookable:
          setQueryVariables({
            bookableFlag: true,
            ...defaultQueryVariables,
          })
          await refetch({
            bookableFlag: true,
            ...defaultQueryVariables,
          })
          break
        case BookableFlagSortingType.UnBookable:
          setQueryVariables({
            bookableFlag: false,
            ...defaultQueryVariables,
          })
          await refetch({
            bookableFlag: false,
            ...defaultQueryVariables,
          })
          break
        default:
          setQueryVariables({
            bookableFlag: null,
            ...defaultQueryVariables,
          })
          await refetch({
            bookableFlag: null,
            ...defaultQueryVariables,
          })
          break
      }
    } catch (e) {
      console.log(e)
    }
  }

  if (error) {
    return Error.render(error)
  }

  return (
    <>
      <div className={clsx('flex', 'justify-between', 'items-center')}>
        <div>
          <Title level={1}>顧客（法人・団体）管理</Title>
        </div>
        <div className={clsx('flex', 'justify-end')}>
          <Space>
            <Button type="primary">
              <Link to="./new">顧客を追加する</Link>
            </Button>
          </Space>
        </div>
      </div>
      <Card
        shadow
        tabList={[
          { key: BookableFlagSortingType.All, tab: '全て' },
          { key: BookableFlagSortingType.Bookable, tab: '予約可能' },
          { key: BookableFlagSortingType.UnBookable, tab: '予約不可' },
        ]}
        defaultActiveTabKey={BookableFlagSortingType.All}
        onTabChange={onTabChange}
      >
        {/* Search bar, Dropdown menu and button */}
        {warning && <div className="ml-2 text-red-600">{warning}</div>}

        <CustomerSearch
          onSearchBack={onSearch}
          searchClear={onSearchClear}
          selectOptions={corporateSelectedOption}
        />
        <Space direction="vertical" size="large">
          <CustomersTable
            customerType={CustomerType.Corporate}
            dataSource={data}
            loading={loading}
          />
          <Pagination
            current={page}
            pageSize={size}
            total={total}
            onChange={paginate}
            showSizeChanger
          />
        </Space>
      </Card>
    </>
  )
}

export default IndexPage
